import { forwardRef, type JSX } from 'react';
import { useTracking, getText } from '../utils';
import { EllipsisVertical } from '@gjensidige/builders-icons';
import { AnimatedArrow } from '../animated-arrow';
import { Loader } from '../loader/loader';
import type {
  PolymorphicComponentPropWithRef,
  PolymorphicRef,
  TrackingProp
} from '../types';
import styles from './button.module.css';
import clsx from 'clsx';

export type ButtonProps<
  Href,
  As extends React.ElementType = Href extends string ? 'a' : 'button'
> = PolymorphicComponentPropWithRef<
  As,
  {
    arrow?: boolean | 'left' | 'right';
    disabled?: boolean;
    href?: Href;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    pressed?: boolean;
    tracking?: TrackingProp;
  } & (
    | {
        fill?: never;
        loading?: never;
        size?: never;
        variant: 'icon';
        widget?: never;
      }
    | {
        fill?: boolean;
        loading?: boolean;
        /**
         * @note Size `'xs'` is for internal applications only.
         */
        size?: 'xs' | 'sm' | 'md';
        variant?: 'critical' | 'primary' | 'action' | 'secondary' | 'unset';
        widget?: boolean;
      }
  )
>;

const loader = (
  <Loader
    aria-label="Loading" // button doesn't have focus when loading so this isn't read, but aria-label is required in Loader
    className={styles.loader}
    size="sm"
    variant="ellipsis"
  />
);

const ButtonComp = <
  Href,
  As extends React.ElementType = Href extends string ? 'a' : 'button'
>(
  {
    arrow,
    as,
    children,
    className,
    disabled = false,
    fill = false,
    icon,
    iconPosition = 'left',
    loading = false,
    onClick,
    pressed = false,
    size = 'md',
    variant: variantRaw = 'primary',
    widget = false,
    tracking = true,
    ...rest
  }: ButtonProps<Href, As>,
  ref: PolymorphicRef<As>
) => {
  const Tag = as || (rest.href ? 'a' : 'button');
  const variant = variantRaw;
  const isAction = variant === 'action';
  const isPrevent = disabled || loading;
  const classes = clsx(
    styles.button,
    styles[widget ? 'critical' : variant], // widget is always critical (for now)
    styles[size],
    className,
    !!fill && styles.fill,
    pressed && styles.pressed,
    widget && styles.widget
  );

  const track = useTracking(tracking);
  const handleClick = (event: React.MouseEvent<As>) => {
    if (isPrevent) return event.preventDefault();
    track({
      eventCategory: `button_${variant}`,
      eventAction: 'click',
      eventLabel: getText(event.currentTarget)
    });
    onClick?.(event); // Track before onClick in case URL changes
  };
  let left: React.ReactNode = null;
  let right: React.ReactNode = null;

  // Add arrows and ensure icon + arrow at same position can not happen
  if (arrow === 'left')
    left = <AnimatedArrow direction="left" className={styles.arrow} />;
  else if (arrow || (isAction && !icon))
    right = <AnimatedArrow direction="right" className={styles.arrow} />;
  if (icon && iconPosition === 'left') left = icon;
  if (icon && iconPosition === 'right') right = icon;

  // Action button changes icon when loading
  if (isAction && loading) {
    if (left) left = loader;
    else if (right) right = loader;
  }

  // Augment rest parameters
  if (!rest.href) Object.assign(rest, { href: undefined }); // Prevent empty href
  if (Tag === 'button') Object.assign(rest, { type: rest.type || Tag }); // Ensure button type
  if (isPrevent)
    Object.assign(rest, {
      [Tag === 'button' ? 'disabled' : 'aria-disabled']: true,
      'aria-busy': loading || undefined,
      tabIndex: -1
    });

  if (variant === 'icon') {
    if (!left && !right) left = <EllipsisVertical />; // Ensure there is always an icon
    if (children)
      console.warn(`<Button variant="icon"> does not support 'children'`);
  }

  if (!children && variant !== 'icon' && !rest['aria-label']) {
    console.warn(`<Button> requires either 'children' or an aria-label`);
  }

  return (
    <Tag className={classes} onClick={handleClick} ref={ref} {...rest}>
      {loading && !isAction && loader}
      {left}
      {children && (
        <span className={styles.content}>{variant !== 'icon' && children}</span>
      )}
      {right}
    </Tag>
  );
};

export const Button = forwardRef<null>(ButtonComp) as <
  Href,
  As extends React.ElementType = Href extends string ? 'a' : 'button'
>(
  props: ButtonProps<Href, As>
) => JSX.Element;

(Button as React.FC).displayName = 'Button';
